'use client';

import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { AngleUpload } from '@/features/angle';
import { LoadingSpinner } from '@/shared/ui/LoadingSpinner';
import { ErrorBoundary } from '@/shared/ui';
import { Button } from '@/shared/ui/shadcn/button';
import {
  modalOverlayTint,
  modalSurface,
} from '@/shared/ui/shadcn/modal-chrome';
import { cn } from '@/shared/lib/cn';
import { ConceptAngleType } from '@/shared/constants';
import type { AngleStoreResponse } from '@/types/concept';

interface MainAngleUploadModalProps {
  conceptId: string | undefined;
  /**
   * Project the upload belongs to. When the concept already has renders the
   * new photo goes to the project library (no conceptId) and a new concept is
   * created from it after validation.
   */
  projectId: string;
  conceptAnglesCount: number;
  hasCompletedRenders: boolean;
  isValidating: boolean;
  validationError: string | null;
  /**
   * STRICT add-view different-building block: when true the error is an
   * overridable different building, so we offer "It's my building" (confirm) +
   * Cancel instead of "Try another image". intent:'view' makes the server
   * hard-block (no auto-route to References) in this concept add-view path.
   */
  canConfirmBuilding?: boolean;
  isConfirmingBuilding?: boolean;
  onConfirmBuilding?: () => void;
  /**
   * Current count of angles in the project library. Used for pre-flight
   * limit check before upload.
   */
  currentProjectAngleLibraryCount?: number;
  onProjectAngleLibraryLimitReached?: () => void;
  onClose: () => void;
  onSuccess: (angleResponse: AngleStoreResponse | AngleStoreResponse[]) => void;
  onClearError: () => void;
  onLimitReached: () => void;
}

export function MainAngleUploadModal({
  conceptId,
  projectId,
  conceptAnglesCount,
  hasCompletedRenders,
  isValidating,
  validationError,
  canConfirmBuilding = false,
  isConfirmingBuilding = false,
  onConfirmBuilding,
  currentProjectAngleLibraryCount,
  onProjectAngleLibraryLimitReached,
  onClose,
  onSuccess,
  onClearError,
  onLimitReached,
}: MainAngleUploadModalProps) {
  const t = useTranslations('concept');
  const tOptions = useTranslations('render.options');
  const tCommon = useTranslations('common');

  return (
    <div
      className={cn(
        'fixed inset-0 flex items-center justify-center p-4 z-50',
        modalOverlayTint
      )}
    >
      <div className={cn('rounded-3xl p-6 max-w-lg w-full', modalSurface)}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sheet-title">{t('mainAngleUpload.title')}</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground/80"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {!isValidating && !validationError && (
          <p className="text-muted-foreground mb-4">
            {hasCompletedRenders
              ? t('mainAngleUpload.descriptionNewConcept')
              : t('mainAngleUpload.descriptionSetMain')}
          </p>
        )}

        {isValidating && (
          <div className="mb-4">
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
            <p className="text-form-help text-muted-foreground text-center">
              {t('mainAngleUpload.validatingImage')}
            </p>
          </div>
        )}

        {validationError && (
          <div className="mb-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-destructive me-2" />
                <div className="flex-1">
                  <h3 className="text-form-label text-destructive">
                    {t('mainAngleUpload.validationFailedTitle')}
                  </h3>
                  <p className="mt-1 text-form-help text-destructive/90">
                    {validationError}
                  </p>
                </div>
              </div>
            </div>
            {canConfirmBuilding ? (
              // STRICT different-building block: only "It's my building" (→ valid
              // view) and Cancel (default: nothing added). No reference / new-
              // building / re-type options here — those belong to lenient intake.
              <div className="mt-3 flex flex-col gap-2">
                <Button
                  onClick={onConfirmBuilding}
                  disabled={isConfirmingBuilding}
                >
                  {isConfirmingBuilding ? (
                    <Loader2 className="h-4 w-4 me-2 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 me-2" />
                  )}
                  {tOptions('itsMyBuilding')}
                </Button>
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isConfirmingBuilding}
                >
                  {tCommon('cancel')}
                </Button>
              </div>
            ) : (
              <button
                onClick={onClearError}
                className="mt-3 w-full bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90"
              >
                {t('mainAngleUpload.tryAnotherImage')}
              </button>
            )}
          </div>
        )}

        {!isValidating && !validationError && (
          <ErrorBoundary>
            <AngleUpload
              conceptId={hasCompletedRenders ? undefined : conceptId}
              projectId={projectId}
              onSuccess={onSuccess}
              onError={onClose}
              allowMultiple={false}
              showValidation={false}
              angleType={ConceptAngleType.MAIN}
              onLimitReached={onLimitReached}
              currentAngleCount={conceptAnglesCount - 1}
              currentProjectAngleLibraryCount={currentProjectAngleLibraryCount}
              onProjectAngleLibraryLimitReached={
                onProjectAngleLibraryLimitReached
              }
            />
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
}
